import { CancelToken } from 'axios';
import { Employee, User } from '../../utils/AccountsAPI';
import { CreatedUpdatedTable } from '../../utils/APIReturnTypes';
import AxiosConfig from '../../utils/AxiosConfig';
import { getUTCDateString, addDays } from '../../utils/Functions';
import { FileUploads } from '../files/FileAPI';

const axiosInstance = AxiosConfig.instance();

/*----------------------------------Credit Cards------------------------------------------*/

// Assignments
export function getAllCardAssignments(token?: CancelToken): Promise<CreditCardAssignment[]> {
  const url = '/company/cards/assignments';

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function getCardAssignmentForUser(employeeNumber: string, token?: CancelToken): Promise<CreditCardAssignment[]> {
  const url = '/company/cards/assignments/' + employeeNumber;

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function putCardAssignmentForUser(employeeNumber: string, assignment: CreditCardAssignment): Promise<CreditCardAssignment> {
  const url = '/company/cards/assignments/' + employeeNumber;

  return axiosInstance.put(url, assignment).then((response) => response.data);
}

export function postCardAssignment(assignment): Promise<CreditCardAssignment> {
  const url = '/company/cards/assignments';

  return axiosInstance.post(url, assignment).then((response) => response.data);
}

export function deleteCardAssignment(id: string): Promise<any> {
  const url = '/company/cards/assignments/' + id;

  return axiosInstance.delete(url);
}

// Transactions
export function getAllCardTransactions(token?: CancelToken): Promise<CreditCardTransaction[]> {
  const url = '/company/cards/transactions';

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function getCardTransactions(beginDate?: Date, endDate?: Date, token?: CancelToken): Promise<CreditCardTransaction[]> {
  var url = '/company/cards/transactions';

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  if (beginDate || endDate) {
    url = url + '?';
  }

  if (beginDate) {
    url = url + 'begindate=' + getUTCDateString(beginDate);
  }

  if (endDate) {
    if (beginDate) {
      url = url + '&';
    }
    url = url + 'enddate=' + getUTCDateString(endDate);
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function getCardTransaction(id: string, token?: CancelToken): Promise<CreditCardTransaction> {
  const url = '/company/cards/transactions/' + id;

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function putCardTransaction(id: string, transaction: CreditCardTransaction): Promise<CreditCardTransaction> {
  const url = '/company/cards/transactions/' + id;

  return axiosInstance.put(url, transaction).then((response) => response.data);
}

export function postCardTransaction(transaction: CreditCardTransaction): Promise<CreditCardTransaction> {
  const url = '/company/cards/transactions';

  return axiosInstance.post(url, transaction).then((response) => response.data);
}

export function postCardTransactions(transactions: CreditCardTransaction[]): Promise<CreditCardTransaction[]> {
  const url = '/company/cards/transactions/bulk';

  return axiosInstance.post(url, transactions).then((response) => response.data);
}

export function deleteCardTransaction(id: string): Promise<any> {
  const url = '/company/cards/transactions/' + id;

  return axiosInstance.delete(url);
}

// Transaction Assignments
export function getCardTransactionAssignments(token?: CancelToken): Promise<CreditCardTransactionAssignment[]> {
  const url = '/company/cards/transactions/assignments';

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function getCardTransactionAssignment(id: string, token?: CancelToken): Promise<CreditCardTransactionAssignment> {
  const url = '/company/cards/transactions/assignments/' + id;

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function putAssignmentsForTransaction(transactionId: string, assignments: CreditCardTransactionAssignment[]): Promise<CreditCardTransaction> {
  const url = '/company/cards/transactions/' + transactionId + '/assignments';

  return axiosInstance.put(url, assignments).then((response) => response.data);
}

export function deleteCardTransactionAssignment(id: string): Promise<any> {
  const url = '/company/cards/transactions/assignments/' + id;

  return axiosInstance.delete(url);
}

export function putManagerApprovals(id: string, approved: boolean): Promise<CreditCardTransactionAssignment> {
  var url = '/company/cards/transactions/assignments/' + id + '/managerapproval/' + approved;

  return axiosInstance.put(url).then((response) => response.data);
}

export function putAccountingApprovals(id: string, approved: boolean): Promise<CreditCardTransactionAssignment> {
  var url = '/company/cards/transactions/assignments/' + id + '/accountingapproval/' + approved;

  return axiosInstance.put(url).then((response) => response.data);
}

// Files
export function uploadCardTransactions(file: File): Promise<CreditCardTransaction[]> {
  const url = '/company/cards/transactions/excel';

  var body = new FormData();
  body.append('file', file);

  var config = { headers: { 'Content-Type': 'multipart/form-data' } };

  return axiosInstance.post(url, body, config).then((response) => response.data);
}

export function getCreditCardExportAndDownload(weekEnding: Date, approvedOnly: boolean, unExportedOnly: boolean, token?: CancelToken): Promise<any> {
  const url =
    `/company/cards/transactions/export?beginDate=${getUTCDateString(addDays(weekEnding, -6))}&endDate=${getUTCDateString(weekEnding)}` +
    (approvedOnly ? '&approved=true' : '') +
    (unExportedOnly ? '&unexported=true' : '');

  var config: any = {
    responseType: 'blob',
  };

  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    var filename = response.headers['content-disposition'].substring(response.headers['content-disposition'].indexOf('=') + 1);
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    return response;
  });
}

// Emails
export function emailCCUnprocessedTransactions(token?: CancelToken): Promise<any> {
  const url = '/company/cards/transactions/notifyusers';

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config);
}

/*---------------------------------GL Categories------------------------------------------*/

export function getGLCategories(token?: CancelToken): Promise<GLCategory[]> {
  const url = '/glcategories';

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function getGLCategory(id: string, token?: CancelToken): Promise<GLCategory> {
  const url = '/glcategories/' + id;

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function postGLCategory(category: GLCategory): Promise<GLCategory> {
  const url = '/glcategories';

  return axiosInstance.post(url, category).then((response) => response.data);
}

export function putGLCategory(id: string, category: GLCategory): Promise<GLCategory> {
  const url = '/glcategories/' + id;

  return axiosInstance.put(url, category).then((response) => response.data);
}

export function deleteGLCategory(id: string): Promise<any> {
  const url = '/glcategories/' + id;

  return axiosInstance.delete(url);
}

/*-------------------------------Unassigned-------------------------------------*/

export function getCardTransactionsForUser(id: string, beginDate: Date | string, endDate: Date | string, token: CancelToken): Promise<CreditCardTransaction[]> {
  var url = '/accounts/users/' + id + '/cardtransactions';

  if (beginDate || endDate) {
    url = url + '?';
  }

  if (beginDate) {
    url = url + 'begindate=' + getUTCDateString(beginDate);
  }

  if (endDate) {
    if (beginDate) {
      url = url + '&';
    }
    url = url + 'enddate=' + getUTCDateString(endDate);
  }

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function getUnassignedCardTransactionsForUser(id: string, token: CancelToken): Promise<CreditCardTransaction[]> {
  const url = '/accounts/users/' + id + '/unassignedtransactions';
  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }
  return axiosInstance.get(url, config).then((response) => response.data);
}

export function getUnassignedCardTransactions(token: CancelToken): Promise<CreditCardTransaction[]> {
  const url = '/company/cards/transactions/unassigned';
  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }
  return axiosInstance.get(url, config).then((response) => response.data);
}

/*-------------------------------Interfaces-------------------------------------*/
export interface CreditCardAssignment extends CreatedUpdatedTable {
  id: string;
  userId: string;
  user: User | Employee;
  accountNumber: string;
  cardDisabled: number;
}

export interface CreditCardTransaction extends CreatedUpdatedTable {
  id: string;
  accountNumber: string;
  transactionDate: Date;
  postDate: Date;
  merchantName: string;
  originalAmount: number;
  originalReferenceNumber: string;
  assignments: CreditCardTransactionAssignment[];
}

export interface CreditCardTransactionAssignment extends CreatedUpdatedTable {
  id: string;
  transactionId: string;
  userId?: string;
  user: User | Employee;
  amount: number;
  referenceNumber: string;
  categoryCode?: string;
  category?: string;
  jobNumber?: string;
  costCode?: string;
  dateEntered?: Date;
  lastUpdatedBy?: string;
  lastUpdatedDate?: Date;
  managerApprovedBy?: string;
  managerApprovedDate?: Date;
  accountingApprovedBy?: string;
  accountingApprovedDate?: Date;
  lastExportedDate?: Date;
  glCategoryId?: number;
  glCategory?: GLCategory;
  files?: FileUploads[];
}

export interface CreditCardTransactionAssignmentWithUser extends CreditCardTransactionAssignment {
  user: User | Employee;
}

export interface GLCategory {
  id: number;
  glCode: string;
  categoryName: string;
  e2PartName: string;
}
